/**
 * Pure predicate driving the #154 drag-drop confirmation modal.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (for code reviewers asking "is this overkill?"):
 * --------------------------------------------------------------------------
 *
 * Same pattern as `isUnassignedOpenWork`, `buildDragNDropUpdateFields`,
 * `datesEqual`: change-detection / drop-decision logic gets its own pure
 * function so tests import the EXACT production predicate (no test-helper
 * drift, #73 / #80 / #83 / #84 / #85).
 *
 * --------------------------------------------------------------------------
 * THE BUG #154 ADDRESSES:
 * --------------------------------------------------------------------------
 *
 * When FileMaker auto-creates a Repair Order from an existing invoice, it
 * pre-fills the `installer` field with the payroll# of the installer who
 * did the original work. That payroll# is the operationally-correct default
 * for the repair (same person who's familiar with the job and customer).
 *
 * Before #154, dragging that pre-assigned Repair Order anywhere in the
 * scheduler grid silently overwrote the `installer` field:
 *   - Cell drop on a DIFFERENT installer → installer = drop-target's payroll
 *   - Unassigned-row drop → installer = ""
 *   - RTS drop → installer = ""
 *
 * The user never saw the original payroll# being wiped. This predicate is
 * how `useEffect[dropObject]` in schedule.context.js decides whether to
 * open the #154 confirm modal (asking the user what to do with the
 * pre-assigned installer) instead of silently proceeding.
 *
 * @param {Object|null|undefined} project - The dragged document (typically
 *   from the TanStack `projectListAfterMerge` cache or `scheduleArray`).
 *   Any shape may be passed; defensive against null/undefined/missing fields.
 * @returns {boolean} true if project is a Repair Order whose `installer`
 *   field holds a non-empty value (string or number); false otherwise.
 */
export function isRepairOrderWithInstaller(project) {
  if (!project) return false;
  if (project.classification !== "Repair Order") return false;
  const installer = project.installer;
  if (installer === undefined || installer === null) return false;
  return String(installer).trim() !== "";
}
